import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { css } from '@emotion/css';

const overlayStyle = css`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(15, 10, 5, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out forwards;

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const modalStyle = css`
  background: #fff;
  border-radius: 28px;
  width: 90%;
  max-width: 420px;
  padding: 40px 36px 36px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18);
  position: relative;
  text-align: center;
  animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;

  @keyframes popIn {
    from { opacity: 0; transform: scale(0.88) translateY(20px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

const closeBtnStyle = css`
  position: absolute;
  top: 18px; right: 22px;
  font-size: 18px;
  color: #ccc;
  cursor: pointer;
  border: none;
  background: none;
  line-height: 1;
  transition: color 0.2s;
  &:hover { color: #555; }
`;

const iconCircle = css`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff0e6 0%, #ffd8b8 100%);
  border: 2px solid rgba(241, 102, 2, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 36px;
  animation: shake 0.5s ease 0.3s;

  @keyframes shake {
    0%,100% { transform: rotate(0deg); }
    20%      { transform: rotate(-10deg); }
    40%      { transform: rotate(10deg); }
    60%      { transform: rotate(-6deg); }
    80%      { transform: rotate(6deg); }
  }
`;

const titleStyle = css`
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 10px;
`;

const subtitleStyle = css`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0 0 8px;
  line-height: 1.6;
`;

const batchNameHighlight = css`
  display: inline-block;
  background: linear-gradient(135deg, #fff3e6 0%, #ffe8cc 100%);
  border: 1px solid rgba(241,102,2,0.2);
  border-radius: 8px;
  padding: 4px 14px;
  font-weight: 800;
  color: #f16602;
  font-size: 15px;
  margin: 8px 0 24px;
  font-family: 'Poppins', sans-serif;
`;

const btnRow = css`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const cancelBtnStyle = css`
  flex: 1;
  height: 50px;
  background: transparent;
  border: 1.5px solid #eaeaea;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #888;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #bbb;
    color: #555;
    background: #f9f9f9;
  }
`;

const deleteBtnStyle = (isDeleting) => css`
  flex: 1;
  height: 50px;
  background: ${isDeleting ? '#ffd4b0' : 'linear-gradient(135deg, #ff6b35 0%, #d44000 100%)'};
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  cursor: ${isDeleting ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: ${isDeleting ? 'none' : '0 6px 20px rgba(212, 64, 0, 0.35)'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    ${!isDeleting && `
      transform: translateY(-2px);
      box-shadow: 0 10px 28px rgba(212, 64, 0, 0.45);
    `}
  }
`;

const progressBarWrap = css`
  margin: 20px 0 0;
  background: #f4f4f4;
  border-radius: 99px;
  height: 6px;
  overflow: hidden;
`;

const progressBarFill = css`
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #ff7e14, #d44000);
  animation: progressLoad 1.5s ease-in-out infinite;

  @keyframes progressLoad {
    0%   { width: 0%; }
    50%  { width: 70%; }
    100% { width: 95%; }
  }
`;

const Studentmap = {};

function DeleteBatch(props) {
  const [status, setStatus] = useState('idle'); // idle | deleting | done

  const getToken = async () => {
    setStatus('deleting');
    try {
      const res = await Auth.currentSession();
      const jwt = res.getAccessToken().getJwtToken();

      let i = 0;
      for (i = 0; i < props.students.length; i++) {
        Studentmap[props.students[i]] = 'remove';
      }

      await deleteEvent(props.batch_owner, props.batch_id, jwt);

      if (props.students.length > 0) {
        await updateStudents(props.batch_id, Studentmap, jwt);
      }
    } catch (e) {
      console.error('Delete batch error', e);
      setStatus('idle');
    }
  };

  const updateStudents = async (id, map, jwt) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({ id, members: map }),
    };
    await fetch('https://api.quizwizard.net/quiz/updatestudentsbatch', requestOptions);
  };

  const deleteEvent = async (classowner, batch_id, jwt) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({ classowner, batch_id }),
    };
    await fetch('https://api.quizwizard.net/quiz/deletebatch', requestOptions);
    props.closedeleteevents();
  };

  useEffect(() => {
    document.title = 'Delete Batch';
  }, []);

  const isDeleting = status === 'deleting';

  return (
    <div className={overlayStyle} onClick={!isDeleting ? props.closedeleteeventwindow : undefined}>
      <div className={modalStyle} onClick={(e) => e.stopPropagation()}>
        {!isDeleting && (
          <button className={closeBtnStyle} onClick={props.closedeleteeventwindow}>✕</button>
        )}

        <div className={iconCircle}>🗑️</div>

        <h3 className={titleStyle}>Delete Batch?</h3>
        <p className={subtitleStyle}>You are about to permanently delete</p>
        <div className={batchNameHighlight}>{props.batch_name}</div>
        <p className={subtitleStyle} style={{ marginBottom: 0 }}>
          This will remove all student associations and cannot be undone.
        </p>

        {isDeleting && (
          <div className={progressBarWrap}>
            <div className={progressBarFill} />
          </div>
        )}

        <div className={btnRow}>
          <button
            className={cancelBtnStyle}
            onClick={props.closedeleteeventwindow}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className={deleteBtnStyle(isDeleting)}
            onClick={getToken}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Deleting…
              </>
            ) : (
              'Yes, Delete'
            )}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

export default DeleteBatch;
